import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback } from 'react';
import { ReturnIcon, SquareReturnIcon, useKeyboardNavigation } from '@atlassian/search-dialog';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { AdvancedSearchLink, CenteredAdvancedSearchGroup, AdvancedSearchContent } from '../../common/advanced-search-link';
import { useQuery } from '../query-context';
import { getTrigger, isNewTab, onAdvancedSearchSelected, useAnalytics } from '../../common/analytics';
import { injectIntl } from 'react-intl-next';
import { messages } from '../messages';
import { useProductContext } from '../product-router';
import { ContentWrapper, SearchFooterLabel, SearchFooterLabelWrapper } from './content-wrapper.styled';
import { MultiWorkspaceAdvancedSearchFooter } from '../../common/advanced-search-link';
import { useSessionUserInput } from '../user-input-provider';
const onKeyDown = (e, target) => {
  if (e.key === 'Enter') {
    var _target$firstElementC, _target$firstElementC2;
    e.preventDefault();

    // The ref here is the wrapper span however we simulate a click on the `ResultLinkComponent` that it wraps.
    (_target$firstElementC = target.firstElementChild) === null || _target$firstElementC === void 0 ? void 0 : (_target$firstElementC2 = _target$firstElementC.click) === null || _target$firstElementC2 === void 0 ? void 0 : _target$firstElementC2.call(_target$firstElementC);
  }
};
const onKeyDownWithShift = (e, target) => {
  if (e.key === 'Enter' && e.shiftKey) {
    var _target$firstElementC3, _target$firstElementC4;
    e.preventDefault();

    // The ref here is the wrapper span however we simulate a click on the `ResultLinkComponent` that it wraps.
    (_target$firstElementC3 = target.firstElementChild) === null || _target$firstElementC3 === void 0 ? void 0 : (_target$firstElementC4 = _target$firstElementC3.click) === null || _target$firstElementC4 === void 0 ? void 0 : _target$firstElementC4.call(_target$firstElementC3);
  }
};
export const AdvancedSearchFooterStateless = ({
  query,
  isLoading,
  activeProduct,
  searchFooterLabel,
  intl,
  linkComponent,
  urlGeneratorForNoResultsScreen = () => '',
  squareReturnIcon = false
}) => {
  const urlGenerator = (activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.generateAdvancedSearchUrl) || urlGeneratorForNoResultsScreen;
  const productName = activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.title;
  const workspaces = activeProduct === null || activeProduct === void 0 ? void 0 : activeProduct.workspaces;
  const showSearchOnEnter = !!(activeProduct !== null && activeProduct !== void 0 && activeProduct.showSearchOnEnter);
  const actionSubjectId = `${productName}AdvancedSearchLink`;
  const [isKeyboardHighlighted, ref] = useKeyboardNavigation({
    onKeydownCallback: showSearchOnEnter ? onKeyDownWithShift : onKeyDown
  });
  const {
    resetSearchSession
  } = useSessionUserInput();
  const searchLabel = searchFooterLabel || intl.formatMessage(messages.extensible_advanced_search_footer);
  const {
    fireAnalyticsEvent
  } = useAnalytics();
  const onAdvancedSearchClicked = useCallback(e => {
    resetSearchSession();
    fireAnalyticsEvent(onAdvancedSearchSelected({
      trigger: getTrigger(e),
      actionSubjectId: actionSubjectId,
      isLoading,
      newTab: isNewTab(e)
    }));
  }, [fireAnalyticsEvent, isLoading, actionSubjectId, resetSearchSession]);
  if (workspaces && (workspaces === null || workspaces === void 0 ? void 0 : workspaces.length) > 1) {
    const workspacesWithAdvancedSearchLinks = workspaces.map(workspace => {
      var _activeProduct$genera;
      return {
        product: productName || '',
        content: workspace.name,
        key: workspace.name,
        advancedSearchUrl: (activeProduct === null || activeProduct === void 0 ? void 0 : (_activeProduct$genera = activeProduct.generateAdvancedSearchUrl) === null || _activeProduct$genera === void 0 ? void 0 : _activeProduct$genera.call(activeProduct, query, workspace.baseUrl)) || ''
      };
    });
    return /*#__PURE__*/React.createElement(MultiWorkspaceAdvancedSearchFooter, {
      workspaces: workspacesWithAdvancedSearchLinks,
      linkComponent: linkComponent,
      isLoading: isLoading,
      onClick: (_, e) => {
        onAdvancedSearchSelected({
          actionSubjectId: actionSubjectId,
          trigger: getTrigger(e),
          isLoading,
          newTab: isNewTab(e)
        });
      }
    });
  }
  return /*#__PURE__*/React.createElement("span", {
    ref: ref,
    onClick: onAdvancedSearchClicked,
    role: "none",
    style: {
      display: 'flex',
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement(AdvancedSearchLink, {
    href: urlGenerator(query),
    isKeyboardHighlighted: isKeyboardHighlighted,
    linkComponent: linkComponent
  }, /*#__PURE__*/React.createElement(ContentWrapper, null, /*#__PURE__*/React.createElement(CenteredAdvancedSearchGroup, null, /*#__PURE__*/React.createElement(SearchIcon, {
    size: "small",
    label: searchLabel
  }), /*#__PURE__*/React.createElement(AdvancedSearchContent, null, searchLabel))), showSearchOnEnter ? /*#__PURE__*/React.createElement(SearchFooterLabelWrapper, null, /*#__PURE__*/React.createElement(SearchFooterLabel, null, "Shift"), "+", /*#__PURE__*/React.createElement(ReturnIcon, {
    inverted: isKeyboardHighlighted
  })) : squareReturnIcon ? /*#__PURE__*/React.createElement(SquareReturnIcon, null) : /*#__PURE__*/React.createElement(ReturnIcon, {
    inverted: isKeyboardHighlighted
  })));
};
const AdvancedSearchFooter = ({
  ...rest
}) => {
  const {
    query,
    isLoading
  } = useQuery();
  const {
    getActiveProduct
  } = useProductContext();
  const activeProduct = getActiveProduct();
  return /*#__PURE__*/React.createElement(AdvancedSearchFooterStatelessWithIntl, _extends({}, rest, {
    query: query,
    isLoading: isLoading,
    activeProduct: activeProduct
  }));
};
export const AdvancedSearchFooterStatelessWithIntl = injectIntl(AdvancedSearchFooterStateless);
export default AdvancedSearchFooter;